import { useState } from 'react'
import { Plus, Users as UsersIcon } from 'lucide-react'
import { usePersonas, type Persona } from '../hooks/usePersonas'
import { PersonaCard } from '../components/personas/PersonaCard'
import { PersonaForm, PersonaFormData } from '../components/personas/PersonaForm'
import { EmptyState } from '../components/ui/EmptyState'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { QueryErrorState } from '../components/ui/QueryErrorState'
import { Modal } from '../components/ui/Modal'

export function PersonasPage() {
  const { data: personas, isLoading, isError, error, refetch, isRefetching, createPersona, updatePersona, deletePersona, isCreating, isUpdating, isDeleting } = usePersonas()
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingPersona, setEditingPersona] = useState<Persona | null>(null)
  const [personaToDelete, setPersonaToDelete] = useState<Persona | null>(null)

  const handleCreate = async (data: PersonaFormData) => {
    await createPersona(data)
    setIsFormOpen(false)
  }

  const handleUpdate = async (data: PersonaFormData) => {
    if (!editingPersona) return
    const payload = {
      id: editingPersona.id,
      name: data.name,
      role: data.role,
      description: data.description ?? null,
      expertise_areas: data.expertise_areas ?? [],
      platforms: data.platforms ?? [],
      signature_phrases: data.signature_phrases ?? [],
      voice_config: data.voice_config ?? null,
    }
    await updatePersona(payload)
    setEditingPersona(null)
    setIsFormOpen(false)
  }

  const handleDeleteRequest = (persona: Persona) => {
    setPersonaToDelete(persona)
  }

  const handleConfirmDelete = async () => {
    if (!personaToDelete) return
    await deletePersona(personaToDelete.id)
    setPersonaToDelete(null)
  }

  const handleCancelDelete = () => {
    setPersonaToDelete(null)
  }

  const handleEdit = (persona: Persona) => {
    setEditingPersona(persona)
    setIsFormOpen(true)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setEditingPersona(null)
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Personas</h1>
          <p className="text-slate-500 mt-1">Gestiona las personas AI para generación de contenido</p>
        </div>
        <button
          onClick={() => setIsFormOpen(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Nueva Persona
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : isError && error ? (
        <QueryErrorState
          title="Error al cargar las personas"
          error={error}
          onRetry={() => refetch()}
          isRefetching={isRefetching}
        />
      ) : personas && personas.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {personas.map((persona) => (
            <PersonaCard
              key={persona.id}
              persona={persona}
              onEdit={() => handleEdit(persona)}
              onDelete={() => handleDeleteRequest(persona)}
              isDeleteDisabled={isDeleting && personaToDelete?.id === persona.id}
            />
          ))}
        </div>
      ) : (
        <div className="card">
          <EmptyState
            icon={<UsersIcon className="w-16 h-16" />}
            title="No hay personas configuradas"
            description="Crea tu primera persona AI para comenzar a generar contenido personalizado"
            action={{
              label: 'Crear primera persona',
              onClick: () => setIsFormOpen(true),
            }}
          />
        </div>
      )}

      <PersonaForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={editingPersona ? handleUpdate : handleCreate}
        initialData={editingPersona ? {
          name: editingPersona.name,
          role: editingPersona.role,
          description: editingPersona.description ?? '',
          avatar_url: editingPersona.avatar_url ?? '',
          expertise_areas: editingPersona.expertise_areas ?? [],
          platforms: editingPersona.platforms ?? [],
          signature_phrases: editingPersona.signature_phrases ?? [],
          voice_config: editingPersona.voice_config && typeof editingPersona.voice_config === 'object'
            ? { ...editingPersona.voice_config } as { tone?: string; style?: string; formality?: string }
            : undefined,
        } : undefined}
        isLoading={isCreating || isUpdating}
      />

      <Modal
        isOpen={personaToDelete !== null}
        onClose={handleCancelDelete}
        title="Eliminar persona"
        size="sm"
      >
        <p className="text-slate-600 mb-6">
          ¿Estás seguro de que quieres eliminar esta persona?
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCancelDelete}
            className="btn btn-secondary"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            className="btn bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </button>
        </div>
      </Modal>
    </div>
  )
}
